import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SecondaryButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Users, UserCheck, UserPlus, UserMinus } from 'lucide-react';

interface User {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  isFollowing: boolean;
}

const FollowersFollowing = () => {
  const { type, username } = useParams<{ type: 'followers' | 'following'; username?: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [targetUser, setTargetUser] = useState<any>(null);
  const [followers, setFollowers] = useState<User[]>([]);
  const [following, setFollowing] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'followers' | 'following'>(type || 'followers');

  useEffect(() => {
    if (user) {
      setActiveTab(type as 'followers' | 'following');
      fetchData();
    }
  }, [user, type, username]);

  const fetchData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Get target user ID
      let targetUserId = user.id;
      if (username && username !== user.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url')
          .eq('username', username)
          .single();
        
        if (profile) {
          targetUserId = profile.user_id;
          setTargetUser(profile);
        } else {
          toast({
            title: "Utente non trovato",
            description: `L'utente @${username} non esiste`,
            variant: "destructive",
          });
          navigate('/app/profile');
          return;
        }
      } else {
        setTargetUser(null);
      }

      await Promise.all([
        fetchFollowers(targetUserId),
        fetchFollowing(targetUserId)
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowers = async (targetUserId: string) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', targetUserId);

      if (error) throw error;

      const followerIds = (data || []).map((f: any) => f.follower_id);
      
      if (followerIds.length === 0) {
        setFollowers([]);
        return;
      }

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, username, display_name, avatar_url, bio')
        .in('user_id', followerIds);

      if (profilesError) throw profilesError;

      // Check if current user follows each follower (only if viewing others' followers)
      const followersWithStatus = await Promise.all(
        (profilesData || []).map(async (profile: any) => {
          let isFollowing = false;
          if (targetUserId !== user.id) {
            const { data: followCheck } = await supabase
              .from('follows')
              .select('id')
              .eq('follower_id', user.id)
              .eq('following_id', profile.user_id)
              .maybeSingle();
            isFollowing = !!followCheck;
          }

          return {
            id: profile.user_id,
            username: profile.username,
            display_name: profile.display_name,
            avatar_url: profile.avatar_url,
            bio: profile.bio,
            isFollowing,
          };
        })
      );

      setFollowers(followersWithStatus);
    } catch (error) {
      console.error('Error fetching followers:', error);
    }
  };

  const fetchFollowing = async (targetUserId: string) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', targetUserId);

      if (error) throw error;

      const followingIds = (data || []).map((f: any) => f.following_id);
      
      if (followingIds.length === 0) {
        setFollowing([]);
        return;
      }

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, username, display_name, avatar_url, bio')
        .in('user_id', followingIds);

      if (profilesError) throw profilesError;

      // Check if current user follows each person in following list
      const followingWithStatus = await Promise.all(
        (profilesData || []).map(async (profile: any) => {
          let isFollowing = targetUserId === user.id; // If viewing own following, always true
          if (targetUserId !== user.id) {
            const { data: followCheck } = await supabase
              .from('follows')
              .select('id')
              .eq('follower_id', user.id)
              .eq('following_id', profile.user_id)
              .maybeSingle();
            isFollowing = !!followCheck;
          }

          return {
            id: profile.user_id,
            username: profile.username,
            display_name: profile.display_name,
            avatar_url: profile.avatar_url,
            bio: profile.bio,
            isFollowing,
          };
        })
      );

      setFollowing(followingWithStatus);
    } catch (error) {
      console.error('Error fetching following:', error);
    }
  };

  const handleFollow = async (targetUserId: string, currentlyFollowing: boolean) => {
    if (!user) return;

    try {
      if (currentlyFollowing) {
        // Unfollow
        await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user.id)
          .eq('following_id', targetUserId);
      } else {
        // Follow
        await supabase
          .from('follows')
          .insert({
            follower_id: user.id,
            following_id: targetUserId,
          });
      }

      // Update local state for both followers and following lists
      setFollowers(prev => prev.map(u => 
        u.id === targetUserId ? { ...u, isFollowing: !currentlyFollowing } : u
      ));
      
      setFollowing(prev => prev.map(u => 
        u.id === targetUserId ? { ...u, isFollowing: !currentlyFollowing } : u
      ));

      if (currentlyFollowing && targetUser?.user_id === user.id) {
        // Remove from own following list if unfollowing
        setFollowing(prev => prev.filter(u => u.id !== targetUserId));
      }

      toast({
        title: currentlyFollowing ? "Non segui più questo utente" : "Ora segui questo utente",
        description: currentlyFollowing 
          ? `Non segui più @${followers.find(f => f.id === targetUserId)?.username || following.find(f => f.id === targetUserId)?.username}`
          : `Ora segui @${followers.find(f => f.id === targetUserId)?.username || following.find(f => f.id === targetUserId)?.username}`,
      });
    } catch (error) {
      console.error('Error updating follow status:', error);
      toast({
        title: "Errore",
        description: "Impossibile aggiornare lo stato di follow",
        variant: "destructive",
      });
    }
  };

  const UserCard = ({ user: profileUser, showFollowButton = true }: { user: User; showFollowButton?: boolean }) => (
    <Card key={profileUser.id} className="glass border-white/20">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <Link to={`/app/user/${profileUser.username}`} className="flex items-center space-x-3 flex-1 min-w-0">
            <Avatar className="w-12 h-12">
              <AvatarImage src={profileUser.avatar_url || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {profileUser.username.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white truncate">
                {profileUser.display_name || profileUser.username}
              </p>
              <p className="text-sm text-muted-foreground">@{profileUser.username}</p>
              {profileUser.bio && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {profileUser.bio}
                </p>
              )}
            </div>
          </Link>
          
          {showFollowButton && profileUser.id !== user?.id && (
            <div className="flex items-center gap-2 ml-3">
              <SecondaryButton
                size="sm"
                onClick={() => handleFollow(profileUser.id, profileUser.isFollowing)}
                className={profileUser.isFollowing ? "bg-muted hover:bg-destructive" : ""}
              >
                {profileUser.isFollowing ? (
                  <>
                    <UserMinus className="w-4 h-4 mr-1" />
                    Smetti di seguire
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-1" />
                    Segui
                  </>
                )}
              </SecondaryButton>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const getPageTitle = () => {
    if (targetUser) {
      return `${targetUser.display_name || targetUser.username}`;
    }
    return 'Le tue connessioni';
  };

  const getBackPath = () => {
    if (targetUser) {
      return `/app/user/${targetUser.username}`;
    }
    return '/app/profile';
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="h-8 w-32 bg-muted rounded animate-pulse" />
        <Card className="glass border-white/20">
          <CardContent className="p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 mb-4 last:mb-0">
                <div className="w-12 h-12 bg-muted rounded-full animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
                  <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 pb-20 lg:pb-4">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to={getBackPath()}>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {getPageTitle()}
          </h1>
          {targetUser && (
            <p className="text-muted-foreground">@{targetUser.username}</p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'followers' | 'following')} className="space-y-6">
        <TabsList className="w-full glass border-white/20">
          <TabsTrigger value="followers" className="flex-1 data-[state=active]:bg-primary">
            <UserCheck className="w-4 h-4 mr-2" />
            Follower ({followers.length})
          </TabsTrigger>
          <TabsTrigger value="following" className="flex-1 data-[state=active]:bg-primary">
            <UserPlus className="w-4 h-4 mr-2" />
            Seguiti ({following.length})
          </TabsTrigger>
        </TabsList>

        {/* Followers Tab */}
        <TabsContent value="followers" className="space-y-4">
          {followers.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <UserCheck className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  {targetUser ? `${targetUser.display_name || targetUser.username} non ha follower` : 'Nessun follower ancora'}
                </h3>
                <p className="text-muted-foreground">
                  {targetUser 
                    ? 'Quando qualcuno inizierà a seguire questo utente, apparirà qui!'
                    : 'Quando qualcuno inizierà a seguirti, apparirà qui!'
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {followers.map((follower) => (
                <UserCard 
                  key={follower.id} 
                  user={follower} 
                  showFollowButton={targetUser?.user_id !== user.id}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Following Tab */}
        <TabsContent value="following" className="space-y-4">
          {following.length === 0 ? (
            <Card className="glass border-white/20">
              <CardContent className="p-8 text-center">
                <UserPlus className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  {targetUser ? `${targetUser.display_name || targetUser.username} non segue nessuno` : 'Non segui ancora nessuno'}
                </h3>
                <p className="text-muted-foreground">
                  {targetUser 
                    ? 'Questo utente non segue ancora nessuno.'
                    : 'Inizia a seguire altri utenti per vedere i loro contenuti nel tuo feed!'
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {following.map((user) => (
                <UserCard 
                  key={user.id} 
                  user={user} 
                  showFollowButton={targetUser?.user_id !== user.id}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FollowersFollowing;